import { NATURES } from './gameData';

const SECONDS_IN_4_HOURS = 4 * 3600;

const LOW_KEY_NATURES = new Set([
  'Lonely', 'Bold', 'Relaxed', 'Timid', 'Serious',
  'Modest', 'Mild', 'Quiet', 'Bashful', 'Calm', 'Gentle', 'Careful'
]);

export const SKILL_GROUPS = {
  'Berry Burst': ['Berry Burst', 'BerryBurst'],
  'Charge Energy S': ['Charge Energy S', 'ChargeEnergyS'],
  'Charge Strength M': ['Charge Strength M', 'ChargeStrengthM'],
  'Charge Strength S': ['Charge Strength S', 'ChargeStrengthS', 'ChargeStrengthSRange', 'ChargeStrengthSStockpile'],
  'Cooking Assist S': ['Cooking Assist S', 'CookingAssistS', 'BulkUp'],
  'Cooking Power-Up S': ['Cooking Power-Up S', 'Cooking Power-up S', 'CookingPowerUpS', 'Minus'],
  'Dream Shard Magnet S': ['Dream Shard Magnet S', 'DreamShardMagnetS'],
  'Energizing Cheer S': ['Energizing Cheer S', 'EnergizingCheerS', 'EnergizingCheerSNuzzle', 'HealPulse'],
  'Energy for Everyone': ['Energy for Everyone', 'EnergyForEveryone', 'EnergyForEveryoneBerryJuice', 'EnergyForEveryoneLunarBlessing'],
  'Extra Helpful S': ['Extra Helpful S', 'ExtraHelpfulS'],
  'Helper Boost': ['Helper Boost', 'HelperBoost'],
  'Ingredient Draw S': ['Ingredient Draw S', 'IngredientDrawS', 'IngredientDrawSHyperCutter', 'IngredientDrawSSuperLuck'],
  'Ingredient Magnet S': ['Ingredient Magnet S', 'IngredientMagnetS', 'IngredientMagnetSPlus', 'PresentIngredient', 'PresentIngredientMagnetS'],
  'Metronome': ['Metronome'],
  'Skill Copy': ['Skill Copy', 'SkillCopy', 'SkillCopyMimic'],
  'Tasty Chance S': ['Tasty Chance S', 'TastyChanceS'],
  'Versatile': ['Versatile']
};

export const INGREDIENT_MAP = {
  'Apple': 'FANCY_APPLE', 'Avocado': 'GLOSSY_AVOCADO', 'Cacao': 'SOOTHING_CACAO', 'Coffee': 'ROUSING_COFFEE', 'Corn': 'GREENGRASS_CORN',
  'Egg': 'FANCY_EGG', 'Ginger': 'WARMING_GINGER', 'Herb': 'FIERY_HERB',
  'Honey': 'HONEY', 'Leek': 'LARGE_LEEK', 'Milk': 'MOOMOO_MILK',
  'Mushroom': 'TASTY_MUSHROOM', 'Oil': 'PURE_OIL', 'Potato': 'SOFT_POTATO', 'Pumpkin': 'PLUMP_PUMPKIN',
  'Sausage': 'BEAN_SAUSAGE', 'Soybean': 'GREENGRASS_SOYBEANS', 'Tail': 'SLOWPOKE_TAIL',
  'Tomato': 'SNOOZY_TOMATO'
};

/**
 * Returns the correct Toxtricity form ('toxtricity_low_key' or 'toxtricity_amped')
 * for a given nature name. Single source of truth for Toxel evolution branching.
 */
export function getToxelEvolution(natureName) {
  return LOW_KEY_NATURES.has(natureName) ? 'toxtricity_low_key' : 'toxtricity_amped';
}
// Represents the >=80% energy bracket
const MAX_ENERGY_MULTIPLIER = 0.45; 
// The game engine's hard cap for speed-boosting subskills
const MAX_SUBSKILL_SPEED_REDUCTION = 0.35; 

export function getFrequencyInSeconds(freq) {
  if (!freq) return 3600; 
  if (typeof freq === 'number') return freq;
  const freqString = String(freq);
  if (!freqString.includes(',')) return Number(freqString) || 3600;
  
  const parts = freqString.split(',').map(Number);
  return (parts[0] * 3600) + (parts[1] * 60) + (parts[2] || 0);
}

/**
 * Calculates the mathematically accurate help time of a Pokémon.
 * Applies the 35% subskill cap and multiplicative nature scaling.
 */
export function calculateHelpTime(baseFreq, level, subskills = [], natureName = 'Serious', activeTeamHB = 0) {
  const baseSeconds = getFrequencyInSeconds(baseFreq);
  let subSpeedMod = 0;
  if (subskills.includes('Helping Speed S')) subSpeedMod += 0.07;
  if (subskills.includes('Helping Speed M')) subSpeedMod += 0.14;
  
  // Calculate total Helping Bonus (this Pokémon + team buffs)
  let totalHB = activeTeamHB;
  if (subskills.includes('Helping Bonus')) totalHB += 1;
  subSpeedMod += (totalHB * 0.05);

  // Apply the 35% hard cap
  subSpeedMod = Math.min(subSpeedMod, MAX_SUBSKILL_SPEED_REDUCTION);

  const nature = NATURES[natureName] || {};
  let natureSpeedMod = 0;
  if (nature.up === 'Speed') natureSpeedMod = 0.10;
  if (nature.down === 'Speed') natureSpeedMod = -0.10;

  // The engine scales speed multiplicatively
  const levelAdjustedBase = baseSeconds - Math.floor(baseSeconds * (level - 1) / 501);
  const finalFreq = levelAdjustedBase * (1 - subSpeedMod) * (1 - natureSpeedMod);
  
  // Apply maximum energy bracket
  const effectiveFreq = finalFreq * MAX_ENERGY_MULTIPLIER;
  const helpsPer4Hours = SECONDS_IN_4_HOURS / effectiveFreq;

  return {
    baseSeconds,
    finalFreq,       // Speed at 0% energy
    effectiveFreq,   // Speed at 80%+ energy
    helpsPer4Hours
  };
}

/**
 * Calculates the zero-sum probabilities for ingredients, berries, and skill triggers.
 */
export function calculateProductionRates(baseIngRate, baseSkillRate, subskills = [], natureName = 'Serious', activeSkill = null) {
  const nature = NATURES[natureName] || {};

  // --- INGREDIENTS & BERRIES (Zero-Sum Roll) ---
  let natureIngMod = 1.0;
  if (nature.up === 'Ing') natureIngMod = 1.2;
  if (nature.down === 'Ing') natureIngMod = 0.8;

  let subIngMod = 0;
  if (subskills.includes('Ingredient Finder S')) subIngMod += 0.18;
  if (subskills.includes('Ingredient Finder M')) subIngMod += 0.36;

  // Ensure baseIngRate is treated as a percentage
  const baseIngPct = (baseIngRate || 20) / 100;
  const finalIngRate = baseIngPct * natureIngMod * (1 + subIngMod);
  
  // Berry probability is whatever is left over after the ingredient roll
  const finalBerryRate = Math.max(0, 1 - finalIngRate);

  // --- SKILL TRIGGERS ---
  let natureSkillMod = 1.0;
  if (nature.up === 'Skill') natureSkillMod = 1.2;
  if (nature.down === 'Skill') natureSkillMod = 0.8;

  let subSkillMod = 0;
  if (subskills.includes('Skill Trigger S')) subSkillMod += 0.18;
  if (subskills.includes('Skill Trigger M')) subSkillMod += 0.36;

  let baseSkillPct = (baseSkillRate || 2.0) / 100;
  
  // Optional: Apply specific multiplier overrides for customizable skills here in the future
  // if (activeSkill === 'IngredientMagnetS') {
  //    baseSkillPct *= 1.1; 
  // }

  const finalSkillRate = baseSkillPct * natureSkillMod * (1 + subSkillMod);

  return {
    ingredientRate: finalIngRate,
    berryRate: finalBerryRate,
    skillRate: finalSkillRate
  };
}

/**
 * Calculates the effective Main Skill Level by combining the base level
 * with any active Skill Level Up subskills.
 * Capped at Level 7 based on current game mechanics.
 */
export function getEffectiveSkillLevel(baseLevel, subskills = []) {
  let effectiveLevel = baseLevel || 1;
  
  if (subskills.includes('Skill Level Up S')) effectiveLevel += 1;
  if (subskills.includes('Skill Level Up M')) effectiveLevel += 2;
  
  // Main skills are currently hard-capped at level 7
  return Math.min(effectiveLevel, 7); 
}

/**
 * Full 24-hour production simulation. The single authoritative function for all
 * production calculations — used by both CompareModal and BenchmarkModal.
 */
export const calculateProduction = (species, mon, durationHours = 24, settings = {}) => {
    const {
        energyStrategy = 'natural',
        inventoryMode = 'unlimited'
    } = settings;

    // --- 1. DETERMINE ACTIVE SUBSKILLS ---
    const unlockLevels = [10, 25, 50, 75, 100];
    const activeSubskills = mon.subskills.filter((skill, index) => {
        return skill && mon.level >= unlockLevels[index];
    });

    // --- 2. CALCULATE INVENTORY LIMIT ---
    let carrySize = parseInt(species.carrySize);
    if (activeSubskills.includes('Inventory Up S')) carrySize += 6;
    if (activeSubskills.includes('Inventory Up M')) carrySize += 12;
    if (activeSubskills.includes('Inventory Up L')) carrySize += 18;

    // --- 3. GET MATHS FROM ENGINE ---
    const { finalFreq } = calculateHelpTime(
        species.frequency,
        mon.level,
        activeSubskills,
        mon.nature || 'Serious',
        0
    );

    const activeSkill = mon.custom_skill || species.skill;
    const { ingredientRate, berryRate, skillRate } = calculateProductionRates(
        species.ingredientPercentage,
        species.skillPercentage || species.skillPercent,
        activeSubskills,
        mon.nature || 'Serious',
        activeSkill
    );

    if (!finalFreq) return { berries: 0, ingredients: {}, skills: 0, inventoryFullTime: null };

    const unlockedSlots = [
      { ...mon.ingredients["0"], weight: 1 },
      mon.level >= 30 ? { ...mon.ingredients["30"], weight: 1 } : null,
      mon.level >= 60 ? { ...mon.ingredients["60"], weight: 1 } : null
    ].filter(Boolean);

    let avgIngCount = 1;
    if (unlockedSlots.length > 0) {
        avgIngCount = unlockedSlots.reduce((acc, slot) => acc + slot.count, 0) / unlockedSlots.length;
    }

    const avgItemsPerHelp = (ingredientRate * avgIngCount) + (berryRate * 1);

    // --- 4. SIMULATION LOOP ---
    let totalHelps = 0;
    let sneakyBerries = 0;
    let inventoryFilled = 0;
    let inventoryFullTime = null;

    const totalMinutes = durationHours * 60;
    const stepMinutes = 10;

    const getEnergyAtTime = (minute) => {
        if (energyStrategy === 'zero') return 0;
        if (energyStrategy === 'maintained') {
            if (minute < 960) return 100;
            const decay = (minute - 960) * (100 / (8 * 60));
            return Math.max(0, 100 - decay);
        }
        return Math.max(0, 100 - (minute * (100 / 1440)));
    };

    const getEnergyIntervalMultiplier = (energy) => {
        if (energy >= 80) return 0.45;
        if (energy >= 60) return 0.52;
        if (energy >= 40) return 0.62;
        if (energy >= 20) return 0.71;
        return 1.00;
    };

    for (let time = 0; time < totalMinutes; time += stepMinutes) {
        const currentEnergy = getEnergyAtTime(time);
        const intervalFreq = finalFreq * getEnergyIntervalMultiplier(currentEnergy);
        const helpsInStep = (stepMinutes * 60) / intervalFreq;

        if (inventoryMode === 'capped' && inventoryFilled >= carrySize) {
            sneakyBerries += helpsInStep;
            if (inventoryFullTime === null) inventoryFullTime = time;
        } else {
            totalHelps += helpsInStep;
            inventoryFilled += (helpsInStep * avgItemsPerHelp);
        }
    }

    // --- 5. OUTPUT ---
    const ingredientHelps = totalHelps * ingredientRate;
    const berryHelps = totalHelps * berryRate;
    const skillProcs = totalHelps * skillRate;

    let berriesPerHelp = (species.specialty === 'berry' || species.specialty === 'all') ? 2 : 1;
    if (activeSubskills.includes('Berry Finding S')) berriesPerHelp += 1;

    const totalBerries = Math.round((berryHelps + sneakyBerries) * berriesPerHelp);

    const ingredientBreakdown = {};
    if (unlockedSlots.length > 0) {
        const helpsPerSlot = ingredientHelps / unlockedSlots.length;
        unlockedSlots.forEach(slot => {
            const expectedQty = helpsPerSlot * slot.count;
            ingredientBreakdown[slot.name] = (ingredientBreakdown[slot.name] || 0) + expectedQty;
        });
    }

    Object.keys(ingredientBreakdown).forEach(key => {
        ingredientBreakdown[key] = parseFloat(ingredientBreakdown[key].toFixed(1));
    });

    return {
        berries: totalBerries,
        ingredients: ingredientBreakdown,
        skills: skillProcs.toFixed(1),
        inventoryFullTime: inventoryFullTime !== null ? (inventoryFullTime / 60).toFixed(1) : null
    };
};

/**
 * Evaluates an Eevee's potential and suggests evolutions based on its subskills and nature.
 */

export const evaluateEevee = (mon, effectiveLevel = 50) => {
    // 1. Gather active stats based on level
    const unlockLevels = [10, 25, 50, 75, 100];
    const activeSubskills = mon.subskills.filter((skill, i) => skill && effectiveLevel >= unlockLevels[i]);
    
    const hasTriggerM = activeSubskills.includes("Skill Trigger M");
    const hasTriggerS = activeSubskills.includes("Skill Trigger S");
    const hasBFS = activeSubskills.includes("Berry Finding S");
    const hasHelpBonus = activeSubskills.includes("Helping Bonus");
    
    // Check Nature (Safely checking your NATURES mapping)
    const nature = NATURES[mon.nature] || { up: null, down: null };
    const skillUp = nature.up === "Main skill chance" || nature.up === "Skill";
    const skillDown = nature.down === "Main skill chance" || nature.down === "Skill";

    // 2. Score the Skill capability (The most important metric for Eevee)
    let skillScore = 0;
    if (hasTriggerM) skillScore += 2;
    if (hasTriggerS) skillScore += 1;
    if (skillUp) skillScore += 2;
    if (skillDown) skillScore -= 2;

    let tier = "C";
    let recommendations = [];
    let notes = [];

    // The "Bin" Check
    if (skillDown && !hasBFS) {
        tier = "F";
        recommendations = ["Candy"];
        notes.push("Main Skill Chance Down ruins most Eeveelutions.");
    } 
    // The S-Tier "Specialists"
    else if (skillScore >= 3) {
        tier = "S";
        recommendations = ["Sylveon", "Espeon"];
        
        if (activeSubskills.some(s => s.includes("Ingredient") || s.includes("Inventory L"))) {
            recommendations.unshift("Vaporeon");
            notes.push("Excellent Vaporeon: Has inventory/ingredient skills to support Ingredient Magnet.");
        }
        
        if (activeSubskills.includes("Sleep EXP Bonus") || hasHelpBonus) {
            notes.push("Top tier Sylveon candidate due to team-wide passive buffs.");
        }
    } 
    // The A-Tier "Utility"
    else if (skillScore === 2 || (skillScore === 1 && hasHelpBonus)) {
        tier = "A";
        recommendations = ["Flareon", "Glaceon"];
        notes.push("Great for Pot Expansion. Needs to trigger occasionally, but raw speed or team buffs help.");
        
        if (activeSubskills.some(s => s.includes("Inventory L"))) {
            notes.push("Massive inventory makes it perfect for leaving overnight to trigger pot expansions.");
        }
    }
    // Default / Leftovers
    else {
        tier = "C";
        recommendations = ["Leafeon", "Wait for better"];
        notes.push("Average stats. Keep searching unless you urgently need a specific type.");
    }

    // The BFS Shadow Fix (Independent Check)
    if (hasBFS) {
        if (tier === "C") tier = "A"; // Bump up rubbish skill Eevees to A tier purely for BFS
        if (tier !== "Bin") {
            recommendations.push("Umbreon", "Jolteon");
            notes.push("Berry Finding S makes this an excellent Umbreon for Snowdrop Tundra.");
            if (activeSubskills.some(s => s.includes("Speed"))) {
                notes.push("High speed with BFS is incredible for Jolteon or Umbreon.");
            }
        }
    }

    // Fixed UI Colours (Full Tailwind classes)
    const colorMap = { 
        "S": "bg-green-100 text-green-800 border-green-300", 
        "A": "bg-blue-100 text-blue-800 border-blue-300", 
        "B": "bg-orange-100 text-orange-800 border-orange-300", 
        "C": "bg-gray-100 text-gray-800 border-gray-300", 
        "F": "bg-red-100 text-red-800 border-red-300" 
    };

    return { 
        tier, 
        recommendations: [...new Set(recommendations)], // Ensures no duplicate suggestions
        colorClasses: colorMap[tier] || colorMap["C"], 
        notes 
    };
};